import { action, makeObservable, observable } from 'mobx';

import { IApiClient } from '../../../../app/data/client';
import Logger from '../../../../app/shared/Logger';
import { RelativeUrl } from '../../../../app/shared/Url';
import type {
  PricelistProductData,
  PricelistProductDetailsData,
  PricelistProductFamilyData,
  PricingContext,
  ShoppingContext,
} from '../../../model';
import { ModuleRoutes } from '../../../ModuleRoutes';
import { GetProductDetailsQuery } from '../../../queries';

export default class ProductModelDetailsState {
  @observable.ref
  public data: PricelistProductDetailsData;

  @observable
  public agreementDiscount: number | undefined;

  @observable
  public isLoading = true;

  public detailsRouteUrl: RelativeUrl;

  public constructor(
    private readonly client: IApiClient,
    private readonly pricingContext: PricingContext,
    routes: ModuleRoutes,
    model: PricelistProductData,
    family: PricelistProductFamilyData,
  ) {
    makeObservable(this);

    this.data = {
      ...model,
      id: model.id,
      code: model.code,
      name: model.name,
      brandId: family.brandId,
      brandName: family.brandName,
      types: [],
      sectors: [],
      keyFeatures: '',
      priceGroups: [],
      displayOptions: { showPriceOnDemand: false },
    };
    this.detailsRouteUrl = routes.productDetails(this.data.id);

    this.load();
  }

  @action
  private async load(): Promise<void> {
    try {
      const { productDetails, agreementDiscount } = await this.client.send(
        new GetProductDetailsQuery({
          pricingContext: this.pricingContext,
          productId: this.data.id,
        }),
      );
      this.setData(productDetails, agreementDiscount);
    } catch (error) {
      Logger.error(`Unable to load product details. Product ID: ${this.data.id}. Error: ${error}`);
    } finally {
      this.setIsLoading(false);
    }
  }

  @action
  private setData(data: PricelistProductDetailsData, agreementDiscount: number | undefined) {
    this.data = data;
    this.agreementDiscount = agreementDiscount;
  }

  @action
  private setIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }
}
